import { useCallback } from 'react';
import { Editor, Node, Point, Text as SlateText, Transforms } from 'slate';

import {
  findAncestorByType,
  getCharacterAfterCursor,
  getCharacterBeforeCursor,
  slateToInstructionsData,
  unwrapCurrentNode,
} from 'src/components/RichSlate';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { PROMPT_ACTION } from 'src/data/prompt';
import { openResourceInNewTab } from 'src/lib/browser';
import { captureMessage } from 'src/lib/sentry';
import { SLATE_TEXT_FORMATS } from 'src/lib/slate';

import {
  actionInsertBrandBrief,
  actionInsertExternalChannelItems,
  actionInsertFileResource,
  actionInsertInstruction,
  actionInsertUrl,
  actionInsertVariable,
  actionRemoveFileResource,
} from './lib';

export const useHandlers = ({
  editor,
  attachedUrls,
  attachedResources,
  handleMenuButtonClick,
  handleMenuPropsChange,
  handleCloseMenu,
  onChange,
  onAction,
}) => {
  // Go over types and check how many resources exists for the type
  const validateResourceLimit = useCallback(
    (resourceType) => {
      if (
        resourceType === RESOURCE_CONTENT_TYPE.url &&
        attachedUrls.length >= MAX_RESOURCE_PER_TYPE
      ) {
        onAction(PROMPT_ACTION.displayMessage, {
          message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.url.toUpperCase()}s`,
        });
        return false;
      }

      const resourcesByType = attachedResources.filter(
        ({ contentType }) => contentType === resourceType
      );
      if (resourcesByType.length >= MAX_RESOURCE_PER_TYPE) {
        onAction(PROMPT_ACTION.displayMessage, {
          message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${resourceType}s`,
        });
        return false;
      }

      return true;
    },
    [attachedUrls.length, attachedResources, onAction]
  );

  const handleOpenMenuAtCursor = useCallback(
    (target, menuProps) => {
      handleMenuButtonClick({ currentTarget: target });
      handleMenuPropsChange(menuProps);
    },
    [handleMenuButtonClick, handleMenuPropsChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;
      const { selection } = editor;
      if (!selection) {
        return;
      }

      try {
        const [node, thisNodePath] = Editor.node(editor, selection);

        const { character: characterBeforeCursor } = getCharacterBeforeCursor(editor);
        const { character: characterAfterCursor } = getCharacterAfterCursor(editor);

        let { node: tagNode, path: tagNodePath } = findAncestorByType(
          editor,
          SLATE_TEXT_FORMATS.PROMPT_TAG
        );

        let noLengthCheck = false;

        if (!tagNode && !tagNodePath) {
          // node not found, find it in the children. happens in rare cases
          let thisNode;
          try {
            const theseChildren = Node.children(editor, [thisNodePath[0], thisNodePath[1] - 1]);
            thisNode = theseChildren.next();
            tagNode = thisNode.value[0];
            tagNodePath = thisNode.value[1].slice(0, 2);
            noLengthCheck = true;
          } catch {
            // nothing to do in this case
          }
        }

        if (tagNode) {
          const openingBracketRemovedWithBackspace =
            characterBeforeCursor === '[' && key === 'Backspace' && selection.anchor.offset === 1;
          const openingBracketRemovedWithDelete =
            characterAfterCursor === '[' && key === 'Delete' && selection.anchor.offset === 0;
          const openingBracketRemovedFromStart =
            openingBracketRemovedWithBackspace || openingBracketRemovedWithDelete;

          const closingBracketRemovedWithBackspace =
            characterBeforeCursor === ']' &&
            key === 'Backspace' &&
            (noLengthCheck || selection.anchor.offset === node.text.length);
          const closingBracketRemovedWithDelete =
            characterAfterCursor === ']' &&
            key === 'Delete' &&
            (noLengthCheck || selection.anchor.offset === node.text.length - 1);
          const closingBracketRemovedFromEnd =
            closingBracketRemovedWithBackspace || closingBracketRemovedWithDelete;

          if (openingBracketRemovedFromStart || closingBracketRemovedFromEnd) {
            unwrapCurrentNode(editor, tagNode, tagNodePath);
          }
          if (key === 'Enter' && !noLengthCheck) {
            // enter while on prompt tag is illegal
            event.preventDefault();
            return;
          }
        }

        const specialKeys = [
          'Backspace',
          'Tab',
          'Shift',
          'Control',
          'Alt',
          'CapsLock',
          'Escape',
          'PageUp',
          'PageDown',
          'End',
          'Home',
          'ArrowLeft',
          'ArrowUp',
          'ArrowRight',
          'ArrowDown',
          'Insert',
          'Delete',
          'NumLock',
          'ScrollLock',
          'Pause',
          'ContextMenu',
          'Meta',
          'F1',
          'F2',
          'F3',
          'F4',
          'F5',
          'F6',
          'F7',
          'F8',
          'F9',
          'F10',
          'F11',
          'F12',
          'F13',
          'F14',
          'F15',
        ];
        const isMetaOrCtrlPressed = event.ctrlKey || event.metaKey;
        // Exclude control keys and other special keys
        if (isMetaOrCtrlPressed || specialKeys.includes(key)) {
          return;
        }

        if (selection) {
          const [node, path] = Editor.node(editor, selection); // Get the currently selected node and its path

          // Get the range of the current node
          const start = Editor.start(editor, path);
          const end = Editor.end(editor, path);

          //avoid case of empty state
          if (SlateText.isText(node) && !node.text) {
            return;
          }

          // Check if the entire node is selected
          if (Point.equals(selection.anchor, start) && Point.equals(selection.focus, end)) {
            // Save the position where we want to insert the new character
            const pointBeforeRemoval = Editor.before(editor, start);

            if (path.length > 0) {
              if (
                !Editor.isStart(editor, selection.anchor, path) ||
                !Editor.isEnd(editor, selection.focus, path) ||
                !(node.type === SLATE_TEXT_FORMATS.PARAGRAPH)
              ) {
                Transforms.delete(editor, { at: selection });
              }
            }

            // Restore the selection
            if (pointBeforeRemoval) {
              Transforms.select(editor, pointBeforeRemoval);
            }

            //new character will be added naturally because we dont do event.preventDefault
          }
        }
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'PromptInstructions/useHandlers -> handleKeyDown',
          },
        });
      }
    },
    [editor]
  );

  const handleAction = useCallback(
    (action, data) => {
      switch (action) {
        case PROMPT_ACTION.insertBrandBrief: {
          handleCloseMenu();
          actionInsertBrandBrief(editor, data);
          break;
        }

        case PROMPT_ACTION.insertUrl: {
          if (!validateResourceLimit(RESOURCE_CONTENT_TYPE.url)) {
            return;
          }

          handleCloseMenu();
          actionInsertUrl(editor, data);
          break;
        }

        case PROMPT_ACTION.validateResourceLimit: {
          return validateResourceLimit(data.resourceType);
        }

        case PROMPT_ACTION.insertFileResource: {
          if (!data.skipConfirmation && !validateResourceLimit(data.resource?.contentType)) {
            return;
          }

          // there is only a "uploadId" in case of upload and then we don't want to close menu since it was
          // already closed and now reopened unrelated to this document
          if (!data.uploadId) {
            handleCloseMenu();
          }

          actionInsertFileResource(editor, data);
          break;
        }

        case PROMPT_ACTION.removeFileResource: {
          actionRemoveFileResource(editor, data);
          break;
        }

        case PROMPT_ACTION.insertExternalChannelItems: {
          handleCloseMenu();
          actionInsertExternalChannelItems(editor, data);
          break;
        }

        case PROMPT_ACTION.insertVariable: {
          actionInsertVariable(editor, data);
          break;
        }

        case PROMPT_ACTION.insertInstruction: {
          actionInsertInstruction(editor, data);
          break;
        }

        case PROMPT_ACTION.openResourceInNewTab: {
          openResourceInNewTab({
            url: data.resource.file.originalUrl,
            isImage: data.resource.contentType === RESOURCE_CONTENT_TYPE.image,
          });
          break;
        }

        default:
          break;
      }

      // Next, pass action to parent component
      return onAction(action, data);
    },
    [editor, handleCloseMenu, onAction, validateResourceLimit]
  );

  const handleChange = useCallback(
    (newValue) => {
      try {
        const instructionsData = slateToInstructionsData(newValue);
        onChange(instructionsData.text, instructionsData);
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'InstructionsTextarea/useHandlers -> handleChange',
          },
        });
      }
    },
    [onChange]
  );

  return {
    validateResourceLimit,
    handleOpenMenuAtCursor,
    handleKeyDown,
    handleChange,
    handleAction,
  };
};
